// External Libraries
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  inject
} from '@angular/core';
import { BehaviorSubject, Subscription, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { HttpClient, HttpParams } from '@angular/common/http';

// Services
import { AuthService } from '../../services/auth/auth.service';
import { TenantApiService } from '../../services/tenant-api/tenant-api.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';

// Models
import { Attachment } from '../../models/attachment';

// Environment Variables
import { environment } from '../../environments/environment';
import { ContextHolder } from '@frontegg/angular';
import { CaptureComponent } from '../../components/capture/capture.component';
import { WhatFixService } from '../../services/whatFix/whatfix.service';
import {
  DocumentAnalysisSdk,
  DocTypeMappingSdk,
  DocumentManagementSdk
} from '@softdocs/capture-ui-libraries';
import { BannerComponent } from '../../components/banner/banner.component';
import { Bind } from 'primeng/bind';
import { Toast } from 'primeng/toast';
import { TranslatePipe } from '@ngx-translate/core';
import { UrlVerificationComponent } from '@ai-document-processor/shared-libraries';

@Component({
  selector: 'app-google-addon',
  templateUrl: './google-addon.component.html',
  styleUrls: ['./google-addon.component.scss'],
  imports: [
    BannerComponent,
    CaptureComponent,
    Bind,
    Toast,
    PrimeTemplate,
    TranslatePipe,
    UrlVerificationComponent
  ]
})
export class GoogleAddonComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  tenantService = inject(TenantApiService);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private featureFlagService = inject(FeatureFlagService);
  private http = inject(HttpClient);
  private cdr = inject(ChangeDetectorRef);
  private whatFix = inject(WhatFixService);

  @ViewChild(CaptureComponent) captureComponent!: CaptureComponent;

  private subscription = new Subscription();

  // SDK Instances
  autoclassifierSdk = new DocumentAnalysisSdk(environment.urls.autoclassifierApi);
  docTypeMappingSdk = new DocTypeMappingSdk(environment.urls.autoclassifierApi);
  documentManagementSdk = new DocumentManagementSdk(environment.urls.autoclassifierApi);

  // Observables and Subjects
  attachments$: BehaviorSubject<Attachment[]> = new BehaviorSubject<Attachment[]>([]);
  autoclassificationFlag$ = new BehaviorSubject<boolean>(false);

  // Component State
  isLoading = true;
  isAutoclassificationFlagLoading = true;
  isUnsupportedFile = false;
  showClassificationResults = false;
  isWebApps = false;

  // Data
  attachments: Attachment[] = [];
  documentIds: string[] = [];
  authToken: string = '';
  tenantId: string = '';
  featureFlagErrorText: string = '';
  userToken: string = '';
  user?: any;

  // Flags and Configurations
  isAccessFlagLoading: boolean = true;
  isAutoClassificationEnabled: boolean = false;
  isUrlVerified: boolean = false;
  gmailAddInAccessEnabled: boolean | null = null;

  // Form
  documentForm!: FormGroup;

  /**
   *
   */
  ngOnInit(): void {
    document.body.classList.add('overflow-scroll-issue');
    // Subscribe to route parameters to get document IDs and auth token
    this.subscription.add(
      this.route.queryParams.subscribe(async params => {
        switch (params['action']) {
          case 'SetURL':
            if (params['userToken']) {
              this.userToken = params['userToken'];
            }

            localStorage.removeItem('tenantId');
            localStorage.removeItem('vanityName');
            localStorage.removeItem('contentURL');
            break;
          case 'ChangeURL':
            if (params['userToken']) {
              this.userToken = params['userToken'];
            }

            localStorage.removeItem('tenantId');
            localStorage.removeItem('vanityName');
            break;
          case 'fileAttachments':
            if (params['ref']) {
              const refToken = params['ref'];
              const addonScriptUrl = environment.urls.addonScript;
              const body = new HttpParams().set('ref', refToken).toString();
              const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };

              try {
                const response =
                  (await this.http
                    .post(addonScriptUrl, body, { headers, responseType: 'text' })
                    .toPromise()) || '';

                const data = JSON.parse(response);
                if (data.error) {
                  console.error('Error from add-on:', data.error);
                } else {
                  this.authToken = data.accessToken;
                  if (this.authToken) {
                    this.authService.setAuthToken(this.authToken);
                  }
                  this.tenantId = data.tenantId;
                  localStorage.setItem('tenantId', this.tenantId);
                  this.subscription.add(
                    this.tenantService.getTenantUrls(this.tenantId).subscribe({
                      next: tenantUrls => {
                        localStorage.setItem('vanityName', tenantUrls.vanityId);
                        localStorage.setItem('contentURL', tenantUrls.contentUrl);
                      },
                      error: error => {
                        this.showErrorToast('Failed to get tenant URLs', error);
                      }
                    })
                  );
                  this.documentIds = data.docIds;
                }
              } catch (error) {
                console.error('Error retrieving attachment info from add-on:', error);
              }
            }
            break;
          default:
            break;
        }
        this.checkFeatureFlag();
      })
    );
  }

  /**
   *
   */
  private getRefTokenFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('ref');
  }

  /**
   * Starts the Google Add-on functionality by loading data and initializing feature flags
   */
  startAddOn(): void {
    this.isUrlVerified = true;
    if (!this.authToken) {
      const organization = localStorage.getItem('vanityName');

      this.tenantId = localStorage.getItem('tenantId') || '';

      this.sendTenantInfoToAddon(this.tenantId, organization || '').then(() => {
        window.top?.close();
      });
      return; // Exit early if no auth token
    }

    this.whatFix.initializeWithContext(this.tenantId);

    this.isLoading = false;
    this.cdr.detectChanges();
    try {
      this.captureComponent.startAdHocFiling(this.documentIds, this.authToken);
    } catch (error) {
      console.error('Error loading documents:', error);
      this.showErrorToast(
        'Error loading documents:',
        error instanceof Error ? error.message : String(error)
      );
    }
  }

  /**
   * Run the feature flag check for the provided tenantId
   * @param tenantId the provided tenantId
   */
  runFeatureFlagCheck(tenantId: string): void {
    this.isAccessFlagLoading = true;
    this.featureFlagService
      .initialize({ tenantId })
      .then(() =>
        this.subscription.add(
          this.featureFlagService
            .getFlag('license-GmailAddOn', false)
            .pipe(
              switchMap(flagValue => {
                this.isAccessFlagLoading = false;
                if (flagValue) {
                  this.gmailAddInAccessEnabled = true;
                  this.whatFix.initializeWithContext(this.tenantId);

                  // Then check the auto-classification flag
                  // Important: Only proceed with auto-classification check if Gmail Add-on is enabled
                  this.subscription.add(
                    this.featureFlagService.getFlag('license-IDP', true).subscribe(idpFlagValue => {
                      this.isAutoclassificationFlagLoading = false;
                      this.isAutoClassificationEnabled = Boolean(idpFlagValue);
                      this.autoclassificationFlag$.next(this.isAutoClassificationEnabled);

                      // Start add-on after feature flag check is complete
                      this.startAddOn();
                    })
                  );
                } else {
                  this.isUrlVerified = false;
                  this.gmailAddInAccessEnabled = false;
                  this.featureFlagErrorText = 'addOnFeatureFlagError';
                }
                return of(null);
              })
            )
            .subscribe()
        )
      )
      .catch(() => {
        this.isAccessFlagLoading = false;
        this.isUrlVerified = false;
        this.gmailAddInAccessEnabled = false;
      });
  }

  /**
   * Re-verifies the tenant URL and triggers feature flag check based on the tenant ID
   */
  onSuccessfulVerification(): void {
    const tenantId = localStorage.getItem('tenantId');
    const organization = localStorage.getItem('vanityName');

    if (tenantId && organization) {
      this.isAccessFlagLoading = true;
      this.runFeatureFlagCheck(tenantId);
    }
  }

  /**
   * Signs out the user of the application and redirects them back to the log in page
   */
  signOut(): void {
    const baseUrl = ContextHolder.getContext().baseUrl;
    window.location.href = `${baseUrl}/oauth/logout?post_logout_redirect_uri=${window.location}`;
  }

  /**
   * Check the feature flag based on tenantId from localStorage.
   */
  checkFeatureFlag(): void {
    const tenantId = this.tenantId;
    if (tenantId) {
      this.isAccessFlagLoading = true;
      this.featureFlagService
        .initialize({ tenantId })
        .then(() => {
          // First check the Gmail Add-on access flag
          this.subscription.add(
            this.featureFlagService
              .getFlag('license-GmailAddOn', false)
              .pipe(
                switchMap(flagValue => {
                  this.isAccessFlagLoading = false;
                  if (flagValue) {
                    this.gmailAddInAccessEnabled = true;
                    this.whatFix.initializeWithContext(this.tenantId);

                    // Then check the auto-classification flag
                    // Important: Only proceed with auto-classification check if Gmail Add-on is enabled
                    this.subscription.add(
                      this.featureFlagService
                        .getFlag('license-IDP', true)
                        .subscribe(idpFlagValue => {
                          this.isAutoclassificationFlagLoading = false;
                          this.isAutoClassificationEnabled = Boolean(idpFlagValue);
                          this.autoclassificationFlag$.next(this.isAutoClassificationEnabled);

                          // Start add-on after feature flag check is complete
                          this.startAddOn();
                        })
                    );
                  } else {
                    this.isUrlVerified = false;
                    this.gmailAddInAccessEnabled = false;
                    this.featureFlagErrorText = 'addOnFeatureFlagError';
                  }
                  return of(null);
                })
              )
              .subscribe()
          );
        })
        .catch(() => {
          this.isAccessFlagLoading = false;
          this.isUrlVerified = false;
          this.gmailAddInAccessEnabled = false;
        });
    } else {
      this.isAccessFlagLoading = false;
      this.isUrlVerified = false;
      this.gmailAddInAccessEnabled = null;
    }
  }

  /**
   * Remove URL verificaetion items from local storage and reload the page to trigger the URL verification process
   */
  changeContentUrl(): void {
    localStorage.removeItem('tenantId');
    localStorage.removeItem('vanityName');
    this.signOut();
  }

  /**
   * Shows an error toast message
   * @param toastMessage
   * @param toastData
   */
  showErrorToast(toastMessage: string, toastData?: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: toastMessage,
      key: 'errorToast',
      data: toastData ? toastData : '',
      life: 10000
    });
  }

  /**
   *
   * @param tenantId
   * @param organization
   */
  async sendTenantInfoToAddon(tenantId: string, organization: string) {
    const addonScriptUrl = environment.urls.addonScript;

    const body = new HttpParams()
      .set('tenantId', tenantId)
      .set('organization', organization)
      .set('token', this.userToken)
      .toString();

    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };

    try {
      const response = await this.http
        .post(addonScriptUrl, body, { headers, responseType: 'text' })
        .toPromise();
    } catch (error) {
      console.error('Error sending tenant info to add-on:', error);
    }
  }

  /**
   * Closes the dialog window and signals to close
   */
  close(): void {
    const refToken = this.getRefTokenFromUrl();
    if (refToken) {
      const body = new URLSearchParams();
      body.set('action', 'cleanup');
      body.set('ref', refToken);

      fetch(environment.urls.addonScript, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      })
        .then(response => response.text())
        .then(text => {})
        .catch(err => console.error('Cleanup error:', err))
        .finally(() => window.close());
    }
  }

  /**
   *
   */
  ngOnDestroy(): void {
    document.body.classList.remove('overflow-scroll-issue');
    localStorage.removeItem('vanityName');
    localStorage.removeItem('tenantId');
    this.subscription.unsubscribe();
  }
}
